import {
  CloudSyncOutlined,
  FileUpload,
  GroupAddOutlined,
  LocalPoliceOutlined,
  MultipleStopOutlined,
  Notifications,
  PeopleAltOutlined,
  PollOutlined,
  PriceChangeOutlined,
  SettingsAccessibilityOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import classNames from "classnames";
import { ChevronsUpDown } from "lucide-react";
import type React from "react";
import { Link } from "~/components/ui/core/Link";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/shadcn/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/shadcn/ui/dropdown-menu";
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "~/components/ui/shadcn/ui/sidebar";
import { usePermissions } from "~/hooks/usePermissions";
import { useSession } from "~/hooks/useSession";
import { makePublicAssetUrl } from "~/lib/files/makePublicAssetUrl";
import { useI18n } from "~/lib/i18n/useI18n";

export const SidebarCompany: React.FC = () => {
  const { t } = useI18n();
  const { user } = useSession();
  const { permissions } = usePermissions();
  const { isMobile, open } = useSidebar();

  if (!user) return null;

  const showImportedEmployees = permissions.canAccessIntegrations;
  const showIntegrations = permissions.canAccessIntegrations;
  const showPopulations = true;
  const showMapping = permissions.canAccessRawData;
  const showUsers = permissions.canAccessAccount;
  const showSurveys = permissions.canAccessRawData;
  const showCompensationPolicy = permissions.canAccessSettings;
  const showPermissionsCenter = permissions.canAccessPermissionsCenter;
  const showFileManager = permissions.canAccessFileManager;
  const showDataValidation = permissions.canAccessRawData;
  const showSettings = permissions.canAccessSettings || permissions.canSetupSSO;

  const companyShortName = user.company.name.startsWith("[demo] ")
    ? user.company.name.slice(7, 9)
    : user.company.name.slice(0, 2);

  return (
    <SidebarFooter className={classNames({ "mb-0": !open, "pb-1": open })}>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  {user.company.logo && (
                    <AvatarImage src={makePublicAssetUrl(user.company.logo.path)} alt={user.company.name} />
                  )}
                  <AvatarFallback className="rounded-lg border border-secondary-500">{companyShortName}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.company.name}</span>
                  <span className="truncate text-xs">{t("components.layout.sidebar.account")}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    {user.company.logo && (
                      <AvatarImage src={makePublicAssetUrl(user.company.logo.path)} alt={user.company.name} />
                    )}
                    <AvatarFallback className="rounded-lg">{companyShortName}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user.company.name}</span>
                    <span className="truncate text-xs">{t("components.layout.sidebar.account")}</span>
                  </div>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              {showImportedEmployees && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/integrations", query: { tab: "imported-employees" } }} noLinkStyle>
                    <GroupAddOutlined />
                    {t("components.layout.sidebar.imported-employees")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showIntegrations && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/integrations", query: { tab: "active-integration" } }} noLinkStyle>
                    <CloudSyncOutlined />
                    {t("components.layout.sidebar.integrations")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showPopulations && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/populations" }} noLinkStyle>
                    <SettingsAccessibilityOutlined />
                    {t("components.layout.sidebar.populations")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showMapping && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/mapping" }} noLinkStyle>
                    <MultipleStopOutlined />
                    {t("components.layout.sidebar.mapping")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showUsers && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/users" }} noLinkStyle>
                    <PeopleAltOutlined />
                    {t("components.layout.sidebar.users")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showSurveys && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/surveys" }} noLinkStyle>
                    <PollOutlined />
                    {t("components.layout.sidebar.surveys")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showCompensationPolicy && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/compensation-policy" }} noLinkStyle>
                    <PriceChangeOutlined />
                    {t("components.layout.sidebar.compensation-policy")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showPermissionsCenter && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/permissions-center" }} noLinkStyle>
                    <LocalPoliceOutlined />
                    {t("components.layout.sidebar.permissions-center")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showFileManager && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/file-manager" }} noLinkStyle>
                    <FileUpload />
                    {t("components.layout.sidebar.file-manager")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showDataValidation && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/data-validation" }} noLinkStyle>
                    <Notifications />
                    {t("components.layout.sidebar.data-validation")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showSettings && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/account/settings" }} noLinkStyle>
                    <SettingsOutlined />
                    {t("components.layout.sidebar.settings")}
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
};
